import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { cn } from "@/lib/utils";

/**
 * Elementer inne i raden som har egen handling. Klikk på dem skal ikke også
 * aktivere raden (VIB-2534), uavhengig av om de selv stopper propagering.
 */
const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, [role="button"], [role="link"], [role="checkbox"], [role="menuitem"]';

export interface ClickableRowProps extends Omit<ComponentPropsWithoutRef<"tr">, "onClick"> {
  /** Kalles ved klikk på raden og ved Enter/Space når raden selv har fokus. */
  onActivate: () => void;
  /** "link" når raden navigerer, "button" (default) når den utfører en handling. */
  variant?: "button" | "link";
  /** Sett når raden ekspanderer/kollapser noe; speiles som aria-expanded. */
  expanded?: boolean;
  /** Uten handling: ingen tabIndex, ingen rolle, ingen pekermarkør. */
  disabled?: boolean;
}

/**
 * Tabellrad som kan åpnes med tastatur (VIB-2534). Erstatter `<tr onClick>`
 * uten tabIndex/role/onKeyDown, som Tab hoppet rett forbi.
 */
export const ClickableRow = forwardRef<HTMLTableRowElement, ClickableRowProps>(
  function ClickableRow(
    { onActivate, variant = "button", expanded, disabled = false, className, onKeyDown, children, ...rest },
    ref,
  ) {
    const handleClick = (e: MouseEvent<HTMLTableRowElement>) => {
      if (disabled) return;
      const interactive = (e.target as HTMLElement).closest(INTERACTIVE_SELECTOR);
      if (interactive && interactive !== e.currentTarget && e.currentTarget.contains(interactive)) return;
      onActivate();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTableRowElement>) => {
      onKeyDown?.(e);
      // Kun når raden selv har fokus: Enter i et nestet felt/knapp er deres sak.
      if (disabled || e.defaultPrevented || e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault(); // Space skal ikke scrolle siden
        onActivate();
      }
    };

    return (
      <tr
        ref={ref}
        role={disabled ? undefined : variant}
        tabIndex={disabled ? undefined : 0}
        aria-expanded={expanded}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          !disabled &&
            "cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary",
          className,
        )}
        {...rest}
      >
        {children}
      </tr>
    );
  },
);
